import store from '@/store'

/**
 * 权限指令 v-permission
 * 基于用户权限控制DOM元素的显示/隐藏
 * 如果用户没有对应权限，则移除该DOM元素
 */

// 检查权限的核心函数
function checkPermission(el, binding) {
  const { value } = binding
  const permissions = store.getters.getPermissions
  const storeUser = store.state.storeUser || {}

  // 管理员不受权限约束
  if (storeUser.isAdmin === 1) {
    return
  }

  // 员工需要进行权限检查
  if (value) {
    let hasPermission = false
    
    if (Array.isArray(value)) {
      // 如果是数组，检查是否有任意一个权限
      hasPermission = value.some(code => permissions.includes(code))
    } else if (typeof value === 'string') {
      // 如果是字符串，检查单个权限
      hasPermission = permissions.includes(value)
    } else if (typeof value === 'object') {
      // 如果是对象，支持更复杂的权限检查
      const { codes, requireAll = false } = value
      if (Array.isArray(codes)) {
        hasPermission = requireAll
          ? codes.every(code => permissions.includes(code))
          : codes.some(code => permissions.includes(code))
      }
    }

    if (!hasPermission) {
      // 没有权限则移除元素
      el.parentNode && el.parentNode.removeChild(el)
    }
  } else {
    // 如果没有提供权限码，则移除元素
    el.parentNode && el.parentNode.removeChild(el)
  }
}

// 自定义指令定义
const permission = {
  // 当被绑定的元素插入到 DOM 中时
  inserted(el, binding) {
    checkPermission(el, binding)
  },
  
  // 组件更新时
  update(el, binding) {
    // 如果权限值发生变化，重新检查权限
    if (binding.value !== binding.oldValue) {
      checkPermission(el, binding)
    }
  }
}

/**
 * 权限指令 v-permission-class
 * 根据权限添加或移除CSS类名
 * 如果用户没有对应权限，则添加指定的CSS类名
 */
const permissionClass = {
  bind(el, binding) {
    const { value, arg } = binding
    const permissions = store.getters.getPermissions
    const storeUser = store.state.storeUser || {}
    
    // 管理员不受权限约束
    if (storeUser.isAdmin === 1) {
      return
    }
    
    if (value) {
      let hasPermission = false
      
      if (Array.isArray(value)) {
        hasPermission = value.some(code => permissions.includes(code))
      } else if (typeof value === 'string') {
        hasPermission = permissions.includes(value)
      }
      
      // 默认添加的类名
      const className = arg || 'no-permission'
      
      if (!hasPermission) {
        el.classList.add(className)
      } else {
        el.classList.remove(className)
      }
    }
  },
  
  update(el, binding) {
    if (binding.value !== binding.oldValue) {
      const { value, arg } = binding
      const permissions = store.getters.getPermissions
      
      if (value) {
        let hasPermission = false
        
        if (Array.isArray(value)) {
          hasPermission = value.some(code => permissions.includes(code))
        } else if (typeof value === 'string') {
          hasPermission = permissions.includes(value)
        }
        
        const className = arg || 'no-permission'
        
        if (!hasPermission) {
          el.classList.add(className)
        } else {
          el.classList.remove(className)
        }
      }
    }
  }
}

/**
 * 权限指令 v-permission-disabled
 * 根据权限设置元素的disabled状态
 */
const permissionDisabled = {
  bind(el, binding) {
    const { value } = binding
    const permissions = store.getters.getPermissions
    const storeUser = store.state.storeUser || {}
    
    // 管理员不受权限约束
    if (storeUser.isAdmin === 1) {
      return
    }
    
    if (value) {
      let hasPermission = false
      
      if (Array.isArray(value)) {
        hasPermission = value.some(code => permissions.includes(code))
      } else if (typeof value === 'string') {
        hasPermission = permissions.includes(value)
      }
      
      if (!hasPermission) {
        el.disabled = true
        el.classList.add('permission-disabled')
      } else {
        el.disabled = false
        el.classList.remove('permission-disabled')
      }
    }
  },
  
  update(el, binding) {
    if (binding.value !== binding.oldValue) {
      const { value } = binding
      const permissions = store.getters.getPermissions
      
      if (value) {
        let hasPermission = false
        
        if (Array.isArray(value)) {
          hasPermission = value.some(code => permissions.includes(code))
        } else if (typeof value === 'string') {
          hasPermission = permissions.includes(value)
        }
        
        if (!hasPermission) {
          el.disabled = true
          el.classList.add('permission-disabled')
        } else {
          el.disabled = false
          el.classList.remove('permission-disabled')
        }
      }
    }
  }
}

export default permission

// 导出所有权限指令
export {
  permission,
  permissionClass,
  permissionDisabled
}

/**
 * 使用示例:
 * 
 * 1. 基本用法（隐藏/显示元素）：
 * <button v-permission="'customer:add'">新增客户</button>
 * <button v-permission="['customer:view', 'customer:edit']">查看或编辑客户</button>
 * 
 * 2. 复杂权限检查：
 * <button v-permission="{ codes: ['customer:view', 'customer:edit'], requireAll: true }">
 *   查看且编辑客户
 * </button>
 * 
 * 3. 添加CSS类名：
 * <button v-permission-class:disabled="'customer:add'">新增客户</button>
 * 
 * 4. 禁用元素：
 * <button v-permission-disabled="'customer:add'">新增客户</button>
 */ 


// WEBPACK FOOTER //
// ./src/directives/permission.js